/** @prettier */

import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import FavoritesIndicator from '@surfline/quiver-react/dist/FavoritesIndicator';
import { PremiumCamCTA } from '@surfline/quiver-react/dist';

import { getCamTitle } from '../../utils/getCamTitle';
import ConditionsOverlay from '../ConditionsOverlay';
import CamFiller from './CamFiller';
import HoverMenu from '../HoverMenu';
import spotPropTypes from '../../propTypes/spot';
import conditionClassModifier from '../../utils/conditionClassModifier';
import cameraPropTypes from '../../propTypes/camera';
import MultiCamIcon from './icons/MultiCam';
import { getUserFavorites } from '../../selectors/user';
import { toggleFavoriteSpot } from '../../actions/user';
import createAccessibleOnClick, { BTN } from '../../utils/createAccessibleOnClick';
import { clickedSubscribeCTA } from '../../actions/segmentEvents';

const overlayClass = condition => {
  const conditionClass = condition ? conditionClassModifier(condition) : 'none';
  return classNames({
    'sl-cam-overlay': true,
    [`sl-cam-overlay--${conditionClass}`]: conditionClass,
  });
};

const CamOverlay = ({ spot, camIndex, changeCamAngle, camAngle, showingPremiumCamCTA }) => {
  const dispatch = useDispatch();
  const favorites = useSelector(getUserFavorites);

  const isFavorite = useMemo(() => !!favorites.find(favorite => favorite._id === spot._id), [
    favorites,
    spot._id,
  ]);
  const doToggleFavorite = useCallback(() => dispatch(toggleFavoriteSpot(spot, isFavorite)), [
    dispatch,
    isFavorite,
    spot,
  ]);

  const { isFiller, cameras, conditions } = spot;
  const hasCam = cameras?.length > 0;
  const hasMultipleCams = cameras?.length > 1;
  const menuOptions = hasMultipleCams
    ? cameras.map(cam => ({
        selected: getCamTitle(camAngle?.title) === getCamTitle(cam.title),
        value: cam,
        name: getCamTitle(cam.title),
      }))
    : [];

  return (
    <div className={overlayClass(conditions?.value)}>
      {showingPremiumCamCTA && (
        <PremiumCamCTA
          href="/upgrade"
          camStillUrl={camAngle?.stillUrl}
          onClickPaywall={() =>
            dispatch(
              clickedSubscribeCTA({
                ctaLocation: 'premium only cam - multi',
                category: 'cams & reports',
                pageName: 'multi-cam',
              })
            )}
        />
      )}
      {isFiller && <CamFiller camIndex={camIndex} />}
      <div className="sl-cam-overlay__bottom">
        {!isFiller && !showingPremiumCamCTA && (
          <ConditionsOverlay spot={spot} camAngle={camAngle} />
        )}
      </div>
      <div className="sl-cam-overlay__top">
        {hasMultipleCams && (
          <HoverMenu
            Icon={MultiCamIcon}
            menuTitle="Cam Angle"
            menuOptions={menuOptions}
            onClickMenuItem={changeCamAngle}
            defaultValue={getCamTitle(camAngle?.title)}
            useSelectOnMobile
            selectDescription="- SELECT A CAM ANGLE -"
          />
        )}
        {!hasCam && !isFiller && <div className="sl-cam-overlay__no-cam">no cam</div>}
        <div
          className="sl-cam-overlay__favorite-toggle"
          type="button"
          title={isFavorite ? 'Remove Favorite' : 'Add Favorite'}
          {...createAccessibleOnClick(doToggleFavorite, BTN)}
        >
          {!isFiller && <FavoritesIndicator favorited={isFavorite} />}
        </div>
      </div>
    </div>
  );
};

CamOverlay.propTypes = {
  spot: spotPropTypes.isRequired,
  camIndex: PropTypes.number.isRequired,
  changeCamAngle: PropTypes.func.isRequired,
  camAngle: cameraPropTypes,
  showingPremiumCamCTA: PropTypes.bool,
};

CamOverlay.defaultProps = {
  camAngle: {},
  showingPremiumCamCTA: false,
};

export default CamOverlay;
